import { useMutation } from '@tanstack/react-query';

import { preflightWorkflow } from '@/lib/api/generated/api';
import type { ImportPreflight } from '@/lib/api/generated/model';
import { WorkflowRejectedError } from '../errors';

/**
 * Asks what a file would need here, without importing it (spec 102 US1).
 *
 * A mutation rather than a query, and not because it writes — it deliberately
 * does not. It is an action somebody takes on a file they just chose, with no
 * key worth caching it under: the same file asked about twice should be asked
 * about twice, since the answer depends on what exists in the workspace right
 * now.
 *
 * Failures map as `useImportWorkflow` maps them, so the dialog renders one
 * shape whichever door refused. Only the size limit answers in this feature's
 * rejection shape, though: this door never refuses over a file's *contents* — a
 * report is its answer — so what is left is about the request itself.
 */
export function usePreflightWorkflow(workspaceId: string) {
  return useMutation({
    mutationFn: async (file: File): Promise<ImportPreflight> => {
      const response = await preflightWorkflow(workspaceId, { file });
      if (response.status === 200) return response.data;
      if (response.status === 413) {
        throw new WorkflowRejectedError(response.status, response.data);
      }
      throw new Error('This file could not be checked.');
    },
  });
}
